/* eslint-disable no-restricted-globals, object-shorthand */
import { SkipCondition, UserProfile, QuizPage } from "../types/quiz";

/**
 * Evaluate skip condition based on user profile
 */
export function evaluateSkipCondition(
  condition: SkipCondition,
  userProfile: UserProfile,
): boolean {
  const fieldValue = userProfile[condition.field];
  const targetValue = condition.value;

  switch (condition.operator) {
    case "equals":
      return fieldValue === targetValue;

    case "not_equals":
      return fieldValue !== targetValue;

    case "greater_than":
      return Number(fieldValue) > Number(targetValue);

    case "less_than":
      return Number(fieldValue) < Number(targetValue);

    case "contains":
      if (Array.isArray(fieldValue)) {
        return fieldValue.includes(targetValue);
      }
      if (typeof fieldValue === "string") {
        return fieldValue.includes(targetValue);
      }
      return false;

    default:
      return false;
  }
}

/**
 * Replace dynamic fields like {name}, {age}, {bmi} with actual values
 */
export function replaceDynamicFields<T extends QuizPage>(
  page: T,
  userProfile: UserProfile,
): T {
  const pageStr = JSON.stringify(page);

  // Find all {fieldName} patterns
  const replacedStr = pageStr.replace(/\{(\w+)\}/g, (match, fieldName) => {
    const value = userProfile[fieldName];
    if (value !== undefined && value !== null) {
      return String(value);
    }
    return match; // Keep original if not found
  });

  return JSON.parse(replacedStr);
}

/**
 * Calculate BMI from weight and height
 */
export function calculateBMI(
  weight: { value: number; unit: string },
  height: { value: number; unit: string },
): number {
  let weightKg = weight.value;
  let heightM = height.value;

  // Convert to metric if needed
  if (weight.unit === "lbs") {
    weightKg = weight.value * 0.453592;
  }

  if (height.unit === "ft/in") {
    // Assuming value is in inches total
    heightM = height.value * 0.0254;
  } else if (height.unit === "cm") {
    heightM = height.value / 100;
  }

  const bmi = weightKg / (heightM * heightM);
  return Math.round(bmi * 10) / 10; // Round to 1 decimal
}

/**
 * Get BMI category
 */
export function getBMICategory(bmi: number): string {
  if (bmi < 18.5) return "underweight";
  if (bmi < 25) return "normal";
  if (bmi < 30) return "overweight";
  return "obese";
}

/**
 * Validate email.
 *
 * Stricter than a bare `x@y.z` check: the local part may not start or end
 * with a dot or contain consecutive dots, domain labels may not start or end
 * with a hyphen, and the TLD must be at least two letters. This rejects
 * common dead addresses like `name.@gmail.com` that simpler regexes accept.
 */
export function validateEmail(email: string): boolean {
  const trimmed = email.trim();
  if (trimmed.length > 254) return false;
  const emailRegex =
    /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;
  return emailRegex.test(trimmed);
}

/**
 * Validate text input
 */
export function validateText(
  value: string,
  validation?: {
    required?: boolean;
    minLength?: number;
    maxLength?: number;
  },
): { valid: boolean; error?: string } {
  if (!validation) return { valid: true };

  if (validation.required && !value.trim()) {
    return { valid: false, error: "This field is required" };
  }

  if (validation.minLength && value.length < validation.minLength) {
    return {
      valid: false,
      error: `Minimum ${validation.minLength} characters required`,
    };
  }

  if (validation.maxLength && value.length > validation.maxLength) {
    return {
      valid: false,
      error: `Maximum ${validation.maxLength} characters allowed`,
    };
  }

  return { valid: true };
}

/**
 * Validate number input
 */
export function validateNumber(
  value: number,
  validation?: {
    required?: boolean;
    min?: number;
    max?: number;
  },
): { valid: boolean; error?: string } {
  if (!validation) return { valid: true };

  if (
    validation.required &&
    (value === null || value === undefined || isNaN(value))
  ) {
    return { valid: false, error: "This field is required" };
  }

  if (validation.min !== undefined && value < validation.min) {
    return {
      valid: false,
      error: `Minimum value is ${validation.min}`,
    };
  }

  if (validation.max !== undefined && value > validation.max) {
    return {
      valid: false,
      error: `Maximum value is ${validation.max}`,
    };
  }

  return { valid: true };
}

/**
 * Format currency
 */
export function formatCurrency(
  amount: number,
  currency: string = "USD",
): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
  }).format(amount);
}

/**
 * Convert units
 */
export function convertUnits(
  value: number,
  fromUnit: string,
  toUnit: string,
  type: "weight" | "height",
): number {
  if (type === "weight") {
    if (fromUnit === "lbs" && toUnit === "kg") {
      return value * 0.453592;
    }
    if (fromUnit === "kg" && toUnit === "lbs") {
      return value / 0.453592;
    }
  }

  if (type === "height") {
    if (fromUnit === "cm" && toUnit === "in") {
      return value / 2.54;
    }
    if (fromUnit === "in" && toUnit === "cm") {
      return value * 2.54;
    }
  }

  return value;
}
